import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import Breadcrumb from '../../components/Common/Breadcrumb';
import FeatherIcon from "feather-icons-react";
import { message } from "antd";
import { BASE_URL } from '../../config/api';
// Import services
import {
  listFiles,
  uploadFiles,
  createFolder,
  deleteItem,
  getDownloadUrl,
  listAllFolders
} from "../../services/fileManagerService";

function buildFolderTree(folders) {
  const pathMap = {};
  folders.forEach(folder => {
    pathMap[folder.path] = { ...folder, children: [] };
  });

  let rootChildren = [];

  folders.forEach(folder => {
    const parts = folder.path.split('/');
    if (parts.length === 1) {
      // Top-level folder
      rootChildren.push(pathMap[folder.path]);
    } else {
      // Nested folder
      const parentPath = parts.slice(0, -1).join('/');
      if (pathMap[parentPath]) {
        pathMap[parentPath].children.push(pathMap[folder.path]);
      }
    }
  });

  return [
    { name: 'uploads', path: '', children: rootChildren }
  ];
}

export const FileManager = () => {
  const [currentPath, setCurrentPath] = useState('');
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [showSidebar, setShowSidebar] = useState(true);
  const [folderTree, setFolderTree] = useState([]);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const fileInputRef = useRef(null);
  const [expandedFolders, setExpandedFolders] = useState({ '': true });

  const API_BASE_URL = BASE_URL;

  useEffect(() => {
    loadFolderContents(currentPath);
    loadFolderTree();
  }, [currentPath]);

  const loadFolderContents = async (path) => {
    setLoading(true);
    try {
      const data = await listFiles(path);
      const folders = data.filter(item => item.type === 'folder');
      const files = data.filter(item => item.type !== 'folder');
      
      setFolders(folders);
      setFiles(files);
    } catch (err) {
      message.error(err.message || 'Failed to load folder contents');
    } finally {
      setLoading(false);
    }
  };

  const loadFolderTree = async () => {
    try {
      const folders = await listAllFolders();
      setFolderTree(buildFolderTree(folders));
    } catch {
      setFolderTree([]);
    }
  };

  const navigateToFolder = (folderName) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    setCurrentPath(newPath);
  };

  const navigateToPath = (path) => {
    setCurrentPath(path);
  };

  const goBack = () => {
    const pathParts = currentPath.split('/').filter(Boolean);
    if (pathParts.length > 0) {
      pathParts.pop();
      const newPath = pathParts.join('/');
      setCurrentPath(newPath);
    }
  };

  const goUp = () => {
    const pathParts = currentPath.split('/').filter(Boolean);
    if (pathParts.length > 0) {
      pathParts.pop();
      const newPath = pathParts.join('/');
      setCurrentPath(newPath);
    }
  };

  const toggleItemSelection = (itemName) => {
    setSelectedItems(prev => 
      prev.includes(itemName) 
        ? prev.filter(name => name !== itemName)
        : [...prev, itemName]
    );
  };

  const selectAllItems = () => {
    const allItems = [...folders.map(f => f.path), ...files.map(f => f.path)];
    setSelectedItems(allItems.length === selectedItems.length ? [] : allItems);
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (files.length === 0) return;

    try {
      await uploadFiles(files, currentPath);
      message.success('Files uploaded successfully');
      loadFolderContents(currentPath);
      // Reset file input
      e.target.value = '';
    } catch (err) {
      message.error(err.message || 'Upload failed');
    }
  };

  const deleteSelected = async () => {
    if (selectedItems.length === 0) {
      message.warning('Please select files to delete');
      return;
    }

    try {
      await Promise.all(selectedItems.map(async item => {
        await deleteItem(item);
      }));
      
      message.success('Deleted successfully');
      loadFolderContents(currentPath);
      setSelectedItems([]);
    } catch (err) {
      message.error(err.message || 'Delete failed');
    }
  };

  const downloadSelected = async () => {
    if (selectedItems.length === 0) {
      message.warning('Please select files to download');
      return;
    }

    try {
      selectedItems.forEach(item => {
        const downloadUrl = getDownloadUrl(item);
        window.open(downloadUrl, '_blank');
      });
    } catch (err) {
      message.error(err.message || 'Download failed');
    }
  };

  const createNewFolder = async () => {
    if (!newFolderName.trim()) {
      message.warning('Please enter a folder name');
      return;
    }

    try {
      const folderPath = currentPath ? `${currentPath}/${newFolderName}` : newFolderName;
      await createFolder(folderPath);
      message.success('Folder created successfully');
      setShowNewFolderModal(false);
      setNewFolderName('');
      loadFolderContents(currentPath);
      loadFolderTree(); // Reload folder tree to show new folder
    } catch (err) {
      message.error(err.message || 'Failed to create folder');
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'folder': return 'folder';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return 'image';
      case 'pdf': return 'file-text';
      case 'mp4':
      case 'mov':
      case 'avi': return 'video';
      case 'zip':
      case 'rar': return 'archive';
      default: return 'file';
    }
  };

  const renderFolderTree = (nodes, level = 0) => {
    return nodes.map(node => {
      const isExpanded = !!expandedFolders[node.path];
      const hasChildren = node.children && node.children.length > 0;

      return (
        <div key={node.path} style={{ marginLeft: `${level * 20}px` }}>
          <div
            className={`d-flex align-items-center py-2 px-2 cursor-pointer hover-bg-light ${currentPath === node.path ? 'bg-primary text-white' : ''}`}
            
            onClick={() => {
              if (hasChildren) {
                setExpandedFolders(prev => ({
                  ...prev,
                  [node.path]: !prev[node.path],
                }));
                if (node.path === '') {
                  setCurrentPath('');
                }
              } else {
                navigateToPath(node.path);
              }
            }}
          >
            {hasChildren ? (
              <span
                onClick={e => {
                  e.stopPropagation();
                  setExpandedFolders(prev => ({
                    ...prev,
                    [node.path]: !isExpanded
                  }));
                }}
                style={{ cursor: 'pointer' }}
                aria-label={isExpanded ? "Collapse folder" : "Expand folder"}
              >
                <FeatherIcon icon={isExpanded ? 'chevron-down' : 'chevron-right'} size={16} className="me-1" />
              </span>
            ) : (
              <span style={{ width: 16, display: 'inline-block' }} className="me-1"></span>
            )}

            <FeatherIcon icon={getFileIcon('folder')} size={16} className="me-2" />
            <span className="small">{node.name}</span>
          </div>

          {hasChildren && isExpanded && renderFolderTree(node.children, level + 1)}
        </div>
      );
    });
  };

  const breadcrumbItems = [
    { label: "File Manager", onClick: () => setCurrentPath('') },
    ...currentPath.split('/').filter(Boolean).map((folder, index, arr) => ({
      label: folder,
      onClick: () => setCurrentPath(arr.slice(0, index + 1).join('/'))
    }))
  ];

  return (
    <div>
      <Helmet><title>File Manager | CRM</title></Helmet>
      <Header />
      <Sidebar activeClassName="file-manager"/>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <Breadcrumb items={breadcrumbItems} />
              </div>
            </div>
          </div>

          <div className="row">
            {showSidebar && (
              <div className="col-md-3">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Folders</h5>
                    <button 
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowSidebar(false)}
                    >
                      <FeatherIcon icon="x" size={16} />
                    </button>
                  </div>
                  <div className="card-body p-0" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    {renderFolderTree(folderTree)}
                  </div>
                </div>
              </div>
            )}

            <div className={showSidebar ? 'col-md-9' : 'col-md-12'}>
              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      <div className="btn-group me-2">
                        <button 
                          className="btn btn-outline-secondary"
                          onClick={goBack}
                          disabled={!currentPath}
                        >
                          <FeatherIcon icon="arrow-left" size={16} />
                        </button>
                        <button 
                          className="btn btn-outline-secondary"
                          onClick={goUp}
                          disabled={!currentPath}
                        >
                          <FeatherIcon icon="arrow-up" size={16} />
                        </button>
                      </div>
                      
                      {!showSidebar && (
                        <button 
                          className="btn btn-outline-secondary me-2"
                          onClick={() => setShowSidebar(true)}
                        >
                          <FeatherIcon icon="sidebar" size={16} />
                        </button>
                      )}

                      <div className="btn-group">
                        <button 
                          className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setViewMode('grid')}
                        >
                          <FeatherIcon icon="grid" size={16} />
                        </button>
                        <button 
                          className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setViewMode('list')}
                        >
                          <FeatherIcon icon="list" size={16} />
                        </button>
                      </div>
                    </div>
                    
                    <div className="col-md-6 text-end">
                      <div className="btn-group">
                        <button 
                          className="btn btn-outline-primary"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <FeatherIcon icon="upload" size={16} className="me-1" />
                          Upload
                        </button>
                        <button 
                          className="btn btn-outline-success"
                          onClick={() => setShowNewFolderModal(true)}
                        >
                          <FeatherIcon icon="folder-plus" size={16} className="me-1" />
                          New Folder
                        </button>
                        <button 
                          className="btn btn-primary"
                          onClick={downloadSelected}
                          disabled={selectedItems.length === 0}
                        >
                          <FeatherIcon icon="download" size={16} className="me-1" />
                          Download
                        </button>
                        <button 
                          className="btn btn-danger"
                          onClick={deleteSelected}
                          disabled={selectedItems.length === 0}
                        >
                          <FeatherIcon icon="trash-2" size={16} className="me-1" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="form-check">
                        <input 
                          className="form-check-input" 
                          type="checkbox" 
                          id="selectAll"
                          checked={selectedItems.length > 0 && selectedItems.length === folders.length + files.length}
                          onChange={selectAllItems}
                        />
                        <label className="form-check-label" htmlFor="selectAll">
                          Select All ({selectedItems.length} selected)
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6 text-end">
                      <small className="text-muted">
                        Path: /uploads/{currentPath}
                      </small>
                    </div>
                  </div>

                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      {viewMode === 'grid' && (
                        <div className="row">
                          {folders.map(folder => (
                            <div key={folder.path} className="col-md-2 col-sm-3 col-4 mb-3">
                              <div className={`card file-card ${selectedItems.includes(folder.path) ? 'border-primary' : ''}`}>
                                <div className="card-body text-center p-3">
                                  <div className="form-check position-absolute" style={{ top: '10px', right: '10px' }}>
                                    <input 
                                      className="form-check-input" 
                                      type="checkbox" 
                                      checked={selectedItems.includes(folder.path)}
                                      onChange={() => toggleItemSelection(folder.path)}
                                    />
                                  </div>
                                  <div 
                                    className="cursor-pointer"
                                    onDoubleClick={() => navigateToFolder(folder.name)}
                                  >
                                    <FeatherIcon icon={getFileIcon(folder.type)} size={48} className="text-warning mb-2" />
                                    <h6 className="card-title small mb-1">{folder.name}</h6>
                                    <small className="text-muted">{folder.modified}</small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          {files.map(file => (
                            <div key={file.path} className="col-md-2 col-sm-3 col-4 mb-3">
                              <div className={`card file-card ${selectedItems.includes(file.path) ? 'border-primary' : ''}`}>
                                <div className="card-body text-center p-3">
                                  <div className="form-check position-absolute" style={{ top: '10px', right: '10px' }}>
                                    <input 
                                      className="form-check-input" 
                                      type="checkbox" 
                                      checked={selectedItems.includes(file.path)}
                                      onChange={() => toggleItemSelection(file.path)}
                                    />
                                  </div>
                                  <div className="cursor-pointer">
                                    {file.type === 'image' ? (
                                      <img 
                                        src={`${API_BASE_URL}/uploads/${file.path}`}
                                        alt={file.name}
                                        className="img-fluid mb-2"
                                        style={{ maxHeight: '48px', objectFit: 'cover' }}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'block';
                                        }}
                                      />
                                    ) : (
                                      <FeatherIcon icon={getFileIcon(file.type)} size={48} className="text-info mb-2" />
                                    )}
                                    <div style={{ display: 'none' }}>
                                      <FeatherIcon icon={getFileIcon(file.type)} size={48} className="text-info mb-2" />
                                    </div>
                                    <h6 className="card-title small mb-1">{file.name}</h6>
                                    <small className="text-muted d-block">{file.size}</small>
                                    <small className="text-muted">{file.modified}</small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {viewMode === 'list' && (
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th width="50">
                                  <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    checked={selectedItems.length > 0 && selectedItems.length === folders.length + files.length}
                                    onChange={selectAllItems}
                                  />
                                </th>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Size</th>
                                <th>Modified</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {folders.map(folder => (
                                <tr key={folder.path} className={selectedItems.includes(folder.path) ? 'table-active' : ''}>
                                  <td>
                                    <input 
                                      className="form-check-input" 
                                      type="checkbox" 
                                      checked={selectedItems.includes(folder.path)}
                                      onChange={() => toggleItemSelection(folder.path)}
                                    />
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <FeatherIcon icon={getFileIcon(folder.type)} size={20} className="text-warning me-2" />
                                      <span 
                                        className="cursor-pointer"
                                        onDoubleClick={() => navigateToFolder(folder.name)}
                                      >
                                        {folder.name}
                                      </span>
                                    </div>
                                  </td>
                                  <td><span className="badge bg-warning">Folder</span></td>
                                  <td>{folder.size}</td>
                                  <td>{folder.modified}</td>
                                  <td>
                                    <div className="btn-group">
                                      <button 
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => {
                                          setSelectedItems([folder.path]);
                                          downloadSelected();
                                        }}
                                      >
                                        <FeatherIcon icon="download" size={14} />
                                      </button>
                                      <button 
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => {
                                          setSelectedItems([folder.path]);
                                          deleteSelected();
                                        }}
                                      >
                                        <FeatherIcon icon="trash-2" size={14} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                              
                              {files.map(file => (
                                <tr key={file.path} className={selectedItems.includes(file.path) ? 'table-active' : ''}>
                                  <td>
                                    <input 
                                      className="form-check-input" 
                                      type="checkbox" 
                                      checked={selectedItems.includes(file.path)}
                                      onChange={() => toggleItemSelection(file.path)}
                                    />
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <FeatherIcon icon={getFileIcon(file.type)} size={20} className="text-info me-2" />
                                      <span>{file.name}</span>
                                    </div>
                                  </td>
                                  <td><span className={`badge ${file.type === 'image' ? 'bg-success' : file.type === 'document' ? 'bg-info' : 'bg-secondary'}`}>{file.type}</span></td>
                                  <td>{file.size}</td>
                                  <td>{file.modified}</td>
                                  <td>
                                    <div className="btn-group">
                                      <button 
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => {
                                          setSelectedItems([file.path]);
                                          downloadSelected();
                                        }}
                                      >
                                        <FeatherIcon icon="download" size={14} />
                                      </button>
                                      <button 
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => {
                                          setSelectedItems([file.path]);
                                          deleteSelected();
                                        }}
                                      >
                                        <FeatherIcon icon="trash-2" size={14} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {folders.length === 0 && files.length === 0 && (
                        <div className="text-center py-5">
                          <FeatherIcon icon="folder" size={64} className="text-muted mb-3" />
                          <h5 className="text-muted">No files or folders found</h5>
                          <p className="text-muted">This directory is empty</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input 
        type="file" 
        ref={fileInputRef}
        style={{ display: 'none' }}
        multiple
        onChange={handleFileUpload}
      />

      {/* New Folder Modal */}
      {showNewFolderModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create New Folder</h5>
                <button 
                  type="button" 
                  className="btn-close"
                  onClick={() => {
                    setShowNewFolderModal(false);
                    setNewFolderName('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="folderName" className="form-label">Folder Name</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    id="folderName"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Enter folder name"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        createNewFolder();
                      }
                    }}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowNewFolderModal(false);
                    setNewFolderName('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={createNewFolder}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .file-card {
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .file-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .hover-bg-light:hover {
          background-color: #f8f9fa;
        }
        .table-active {
          background-color: #e3f2fd;
        }
      `}</style>
    </div>
  );
};

export default FileManager;